import { useCallback, useState } from "react";

/**
 * Reveal state for a column of masked tax IDs: a header "Show all / Hide all" toggle
 * (today's behaviour) plus a per-row Show/Hide that flips one row against it.
 * Only used when the person may reveal (Admin); the state is never applied otherwise.
 *
 * It holds row ids only: it never reads, logs or reshapes a row's tax ID. Masking happens
 * only in MaskedTaxId at render time.
 */
export function useTaxIdReveal() {
  const [revealAll, setRevealAll] = useState(false);
  const [flipped, setFlipped] = useState<Set<string>>(() => new Set());

  const toggleAll = useCallback(() => {
    setRevealAll((prev) => !prev);
    setFlipped(new Set());
  }, []);

  const toggleRow = useCallback((rowId: string) => {
    setFlipped((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  const isRevealed = useCallback(
    (rowId: string) => (revealAll ? !flipped.has(rowId) : flipped.has(rowId)),
    [revealAll, flipped]
  );

  return { revealAll, toggleAll, toggleRow, isRevealed };
}

export default useTaxIdReveal;
